import { ModeDocumentsPageType } from '../types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useColumnsBaseTableDocuments } from './useColumnsBaseTableDocuments';
import { MenuItemType } from 'antd/es/menu/interface';

export const useResizableColumnTableDocuments = (
    modeDocument: ModeDocumentsPageType,
    tableRef: React.RefObject<HTMLDivElement>,
    draggable: boolean,
    filterActions?: (actions: MenuItemType[]) => MenuItemType[],
) => {
    const baseColumns = useColumnsBaseTableDocuments(modeDocument, draggable, filterActions);

    const [columns, setColumns] = useState(() => baseColumns);
    const [isResizing, setIsResizing] = useState(false);
    const [resizingColumn, setResizingColumn] = useState<number | null>(null);

    const userWidthsRef = useRef<{ [key: string]: number }>({});

    const resizeStateRef = useRef({
        startX: 0,
        startWidth: 0,
        columnIndex: -1,
        currentWidth: 0,
    });

    const getColumnKey = (col: any, index: number): string => {
        if (col.key) {
            return col.key;
        }

        if ('dataIndex' in col && col.dataIndex) {
            return col.dataIndex;
        }

        return index.toString();
    };

    useEffect(() => {
        setColumns((prevColumns) => {
            const newColumns = baseColumns?.map((col, index) => {
                const colKey = getColumnKey(col, index);
                const userWidth = userWidthsRef.current[colKey];

                return {
                    ...col,
                    width: userWidth || col.width,
                };
            });

            return newColumns;
        });
    }, [baseColumns]);

    const updateTableColumnWidth = useCallback((columnIndex: number, width: number) => {
        if (!tableRef.current) return;

        const colgroup = tableRef.current.querySelector('colgroup');
        if (colgroup) {
            const cols = colgroup.querySelectorAll('col');
            if (cols[columnIndex + 1]) {
                (cols[columnIndex + 1] as HTMLElement).style.width = `${width}px`;
            }
        }

        const thead = tableRef.current.querySelector('thead');
        if (thead) {
            const thElements = thead.querySelectorAll('th');
            if (thElements[columnIndex + 1]) {
                (thElements[columnIndex + 1] as HTMLElement).style.width = `${width}px`;
            }
        }

        const tbody = tableRef.current.querySelector('tbody');
        if (tbody) {
            const rows = tbody.querySelectorAll('tr');
            rows.forEach((row) => {
                const cells = row.querySelectorAll('td');
                if (cells[columnIndex + 1]) {
                    (cells[columnIndex + 1] as HTMLElement).style.width = `${width}px`;
                }
            });
        }
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent, columnIndex: number) => {
        e.preventDefault();
        e.stopPropagation();

        const th = (e.target as HTMLElement).closest('th');
        if (!th) return;

        const startWidth = th.offsetWidth;

        resizeStateRef.current = {
            startX: e.clientX,
            startWidth,
            columnIndex,
            currentWidth: startWidth,
        };

        setIsResizing(true);
        setResizingColumn(columnIndex);

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.body.classList.add('resizing');
    }, []);

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isResizing || resizingColumn === null) return;

            const { startX, startWidth, columnIndex } = resizeStateRef.current;
            const diff = e.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff);

            updateTableColumnWidth(columnIndex, newWidth);

            resizeStateRef.current.currentWidth = newWidth;

            setColumns((prevColumns) => {
                const newColumns = [...prevColumns!];
                if (newColumns[columnIndex]) {
                    newColumns[columnIndex] = {
                        ...newColumns[columnIndex],
                        width: newWidth,
                    };
                }
                return newColumns;
            });
        },
        [isResizing, resizingColumn, updateTableColumnWidth],
    );

    const handleMouseUp = useCallback(() => {
        if (isResizing) {
            setIsResizing(false);
            setResizingColumn(null);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.body.classList.remove('resizing');

            const { columnIndex, currentWidth } = resizeStateRef.current;
            if (columnIndex >= 0 && currentWidth > 0) {
                const colKey = getColumnKey(columns?.[columnIndex], columnIndex);
                userWidthsRef.current[colKey] = currentWidth;
            }
        }
    }, [isResizing, columns]);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove, { passive: false });
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isResizing, handleMouseMove, handleMouseUp]);

    const columnsWithResize = columns?.map((col, index) => ({
        ...col,
        onHeaderCell: () => ({
            width: col.width,
            columnIndex: index,
            onResize: () => {},
        }),
    }));

    return {
        handleMouseDown,
        columnsWithResize,
    };
};
